import { startCase, truncate, isArray } from "lodash";
import { useState } from "react";
import { Table } from "semantic-ui-react";
import { isMultipleChoiceOption } from "../models/AppConfiguration";
import GridActionButton from "./GridActionButton";
import SetConfigurationValueModal from "./SetConfigurationValueModal";

interface Props {
  /** The feature configuration object (e.g. GisBoundaryFeatureData, GangMembersFeatureData, etc.) */
  feature: Record<string, any>;
  /** Called with the updated feature object when a property is changed */
  onChange: (updated: Record<string, any>) => void;
  /** Keys to exclude from the grid (e.g. "enabled" which is handled by the toggle) */
  excludeKeys?: string[];
  nullableValueTypes?: Record<string, "number" | "boolean" | "string">;
}

const formatValue = (value: unknown): string => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (isMultipleChoiceOption(value)) return value.value;
  if (isArray(value)) return `[${value.length} item${value.length !== 1 ? "s" : ""}]`;
  if (value == null) return "—";
  return truncate(String(value), { length: 60 });
};

/**
 * A grid component that displays the properties of a feature configuration object,
 * allowing each property to be edited inline via a modal.
 * Follows the same pattern as OrganizationConfigurationGrid.
 */
const FeatureConfigurationGrid = ({ feature, onChange, excludeKeys = ["enabled"], nullableValueTypes = {} }: Props) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const entries = Object.entries(feature).filter(([key]) => !key.startsWith("__") && !excludeKeys.includes(key));

  const handleEdit = (key: string) => {
    setEditingKey(key);
  };

  const handleSubmit = (newValue: object) => {
    if (editingKey) {
      onChange({ ...feature, [editingKey]: newValue });
      setEditingKey(null);
    }
  };

  const handleClose = () => {
    setEditingKey(null);
  };

  return (
    <>
      <Table basic="very" compact>
        <Table.Body>
          {entries.map(([key, value]) => (
            <Table.Row key={key}>
              <Table.Cell width="6">{startCase(key).toLowerCase()}</Table.Cell>
              <Table.Cell width="8">
                {formatValue(value)}
                <GridActionButton
                  rowId={key}
                  items={[
                    {
                      key: "edit",
                      icon: "edit",
                      content: "Edit",
                      callback: () => handleEdit(key),
                    },
                  ]}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <SetConfigurationValueModal
        open={editingKey !== null}
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitInProgress={false}
        title="Edit"
        initialValue={editingKey ? feature[editingKey] : null}
        configKey={editingKey ?? undefined}
        nullableValueTypes={nullableValueTypes}
      />
    </>
  );
};

export default FeatureConfigurationGrid;
